import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import Seo from "../components/Seo";
import AirportPicker from "../components/AirportPicker";
import CalendarCell from "../components/CalendarCell";
import ProGate, { useIsPro } from "../components/ProGate";
import { CABINS, monthAwardCalendar, fmtPoints, isDateLocked, FREE_WINDOW_DAYS } from "../data/mock";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function useMonthNav() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthIndex, setMonthIndex] = useState(now.getMonth());
  const shift = (delta) => {
    const d = new Date(year, monthIndex + delta, 1);
    setYear(d.getFullYear());
    setMonthIndex(d.getMonth());
  };
  return { year, monthIndex, shift };
}

function CalendarControls({ from, setFrom, to, setTo, cabin, setCabin }) {
  return (
    <div className="mt-6 flex flex-wrap items-end gap-3">
      <AirportPicker label="From" value={from} onChange={setFrom} testId="calendar-from-input" size="sm" className="w-full sm:w-56" />
      <AirportPicker label="To" value={to} onChange={setTo} testId="calendar-to-input" size="sm" className="w-full sm:w-56" />
      <div className="flex items-center gap-1.5">
        {CABINS.map((c) => (
          <button key={c.key} onClick={() => setCabin(c.key)} className={`rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${cabin === c.key ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"}`}>{c.label}</button>
        ))}
      </div>
    </div>
  );
}

function LockedBanner({ count }) {
  return (
    <Link to="/pricing" data-testid="calendar-locked-banner" className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 transition-colors hover:bg-blue-100">
      <span className="flex items-center gap-2"><Lock className="h-4 w-4 shrink-0" /> {count} day{count === 1 ? "" : "s"} this month are beyond the free {FREE_WINDOW_DAYS}-day window.</span>
      <span className="shrink-0 font-bold">Unlock with PRO →</span>
    </Link>
  );
}

export default function Calendar() {
  const [from, setFrom] = useState("JFK");
  const [to, setTo] = useState("LHR");
  const [cabin, setCabin] = useState("business");
  const { year, monthIndex, shift } = useMonthNav();
  const isPro = useIsPro();

  const cal = useMemo(() => monthAwardCalendar(from, to, cabin, year, monthIndex), [from, to, cabin, year, monthIndex]);
  const blanks = Array.from({ length: new Date(year, monthIndex, 1).getDay() });
  const visibleDays = cal.days.filter((d) => d.points != null && !isDateLocked(d.date, isPro));
  const lockedCount = cal.days.filter((d) => d.points != null && isDateLocked(d.date, isPro)).length;
  const allLocked = lockedCount > 0 && visibleDays.length === 0;
  const cheapest = [...visibleDays].sort((a, b) => a.points - b.points)[0];

  return (
    <main className="min-h-[70vh] bg-slate-50">
      <Seo title="Award Calendar — cheapest points day by day | FindMyFlight" description="See the cheapest award points for every day of the month on your route with the FindMyFlight award calendar heatmap." path="/calendar" />
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-8">
          <div className="flex items-center gap-2 text-blue-600"><CalendarDays className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Award Calendar</span></div>
          <h1 className="mt-2 text-3xl font-extrabold text-slate-900">Cheapest points, day by day</h1>
          <p className="mt-2 max-w-2xl text-slate-600">Pick a route and cabin to see a month-at-a-glance heatmap of the lowest award pricing each day. Greener is cheaper.</p>
          <CalendarControls from={from} setFrom={setFrom} to={to} setTo={setTo} cabin={cabin} setCabin={setCabin} />
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <button onClick={() => shift(-1)} aria-label="Previous month" data-testid="calendar-prev" className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
            <div className="text-center">
              <h2 className="text-lg font-bold text-slate-900">{MONTHS[monthIndex]} {year}</h2>
              <p className="text-xs text-slate-500">{from} → {to} · {CABINS.find((c) => c.key === cabin)?.label} · {new Intl.NumberFormat().format(cal.distance)} mi</p>
            </div>
            <button onClick={() => shift(1)} aria-label="Next month" data-testid="calendar-next" className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
          </div>

          <div className="mt-5 grid grid-cols-7 gap-1.5 text-center text-[11px] font-semibold uppercase text-slate-400">
            {WEEKDAYS.map((w) => <div key={w}>{w}</div>)}
          </div>
          <ProGate locked={allLocked} count={lockedCount}>
            <div className="mt-1.5 grid grid-cols-7 gap-1 sm:gap-1.5">
              {blanks.map((_, i) => <div key={`blank-${i}`} />)}
              {cal.days.map((d) => (
                <CalendarCell key={d.date} d={d} locked={d.points != null && isDateLocked(d.date, isPro)} isCheapest={cheapest?.date === d.date} min={cal.min} max={cal.max} from={from} to={to} />
              ))}
            </div>
          </ProGate>
          {lockedCount > 0 && !allLocked && <LockedBanner count={lockedCount} />}

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Cheaper</span>
              <div className="h-3 w-40 rounded-full" style={{ background: "linear-gradient(90deg, hsl(140 75% 82%), hsl(70 75% 82%), hsl(0 75% 85%))" }} />
              <span>Pricier</span>
            </div>
            {cheapest && (
              <p className="text-sm text-slate-600">Best day: <span className="font-bold text-blue-600">{MONTHS[monthIndex]} {cheapest.day}</span> at <span className="font-bold">{fmtPoints(cheapest.points)} pts</span></p>
            )}
          </div>
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">Award pricing refreshes continuously — always confirm the final price with the airline before transferring points.</p>
      </div>
    </main>
  );
}
